import { Component, effect, inject, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { EntityType, Role } from '@gen-common/enums';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MegaMenuItem, MenuItem } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { MegaMenu } from 'primeng/megamenu';
import { MenuModule } from 'primeng/menu';
import { TieredMenu } from 'primeng/tieredmenu';
import { filter, map, mergeMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import Session from 'supertokens-web-js/recipe/session';

import { AuthService } from './auth/auth.service';
import { PermissionService } from './features/setting/permission.service';
import { LanguageComponent } from './languages.component';
import { CleanerCacheRealtimeService } from './realtime/cleaner-cache-realtime.service';
import { MenuBadgeRealtimeService } from './realtime/menu-badge-realtime.service';
import { RealtimeSocketService } from './realtime/realtime-socket.service';

import { PermissionKey } from '@/shared/models/permission.structure';

type CustomMenuItem = {
  label: string;
  icon: string;
  badge?: string;
  routerLink?: string;
  entity?: EntityType;
  permissionBlock?: string;
  roles?: Role[];
  requiresEmployee?: boolean;
  preserveGroup?: boolean;
  items?: CustomMenuItem[];
};
@Component({
  selector: 'app-layout',
  imports: [RouterOutlet, RouterLink, TranslocoModule, MenuModule, TieredMenu, MegaMenu, LanguageComponent, ConfirmDialog],
  templateUrl: './app-layout.component.html'
})
export class AppLayoutComponent implements OnInit {
  userId: string | undefined;
  userProfile: MegaMenuItem[] | undefined;
  menuItems: MenuItem[] = [];

  pageTitle = '';
  sidebarOpen = false;
  private currentPath = '';
  private routeTitleLink: string | null = null;

  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);
  protected authService: AuthService = inject(AuthService);
  private permissionService: PermissionService = inject(PermissionService);
  private translocoService: TranslocoService = inject(TranslocoService);
  private cleanerCacheRealtimeService = inject(CleanerCacheRealtimeService);
  private menuBadgeRealtimeService = inject(MenuBadgeRealtimeService);
  private realtimeSocketService = inject(RealtimeSocketService);

  constructor() {
    effect(() => {
      this.menuBadgeRealtimeService.pendingCleanings();
      this.menuBadgeRealtimeService.pendingTasks();
      this.menuBadgeRealtimeService.pendingPayments();
      this.menuBadgeRealtimeService.problemIssues();
      this.updateMenuItems();
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let child = this.route.firstChild;
          while (child?.firstChild) {
            child = child.firstChild;
          }
          return child;
        }),
        filter((r) => !!r),
        mergeMap((r) => r!.data)
      )
      .subscribe((data) => {
        this.pageTitle = data['pageTitle'] ?? '';
        this.routeTitleLink = data['pageTitleLink'] ?? null;
        this.currentPath = this.router.url.split(/[?#]/)[0].replace(/\/$/, '');
        this.sidebarOpen = false;
      });
  }

  get isEntityFormPage(): boolean {
    return !!this.entityTableRoute;
  }

  get pageTitleLink(): string {
    return this.entityTableRoute ?? '/';
  }

  private get entityTableRoute(): string | null {
    if (this.routeTitleLink) {
      return this.routeTitleLink;
    }
    const segments = this.currentPath.split('/').filter(Boolean);
    const lastSegment = segments.at(-1);
    if (!lastSegment || segments.length < 2) {
      return null;
    }
    if (lastSegment !== 'new' && !/^\d+$/.test(lastSegment)) {
      return null;
    }

    return '/' + segments.slice(0, -1).join('/');
  }

  async ngOnInit(): Promise<void> {
    this.translocoService.selectTranslation().subscribe(() => {
      this.updateByLanguageUpsert();
    });

    await this.authService.loadUser();
    this.userId = this.authService.currentUser?.userId ?? (await Session.getUserId().catch(() => undefined));
    this.updateByLanguageUpsert();
    if (!this.isMobileView()) {
      this.cleanerCacheRealtimeService.start();
      this.menuBadgeRealtimeService.start(this.canViewIssueBadge());
    }
  }

  isMobileView() {
    const user = this.authService.currentUser;
    return user?.roles.length === 1 && (user.roles.includes(Role.CLEANER) || user.roles.includes(Role.TECHNICIAN));
  }

  updateByLanguageUpsert() {
    this.updateMenuItems();

    if (this.authService.currentUser) {
      this.updateUserProfile();
    }
  }

  updateMenuItems() {
    const roles = this.authService.roles;
    const justOwner = roles.length === 1 && roles.includes(Role.OWNER);
    const useCleaningMobileRoute = roles.length === 1 && roles.includes(Role.CLEANER);
    const useTaskMobileRoute = roles.length === 1 && roles.includes(Role.TECHNICIAN);
    const countOfPendingCleanings = this.menuBadgeRealtimeService.pendingCleanings();
    const countOfPendingTasks = this.menuBadgeRealtimeService.pendingTasks();
    const countOfPendingPayments = this.menuBadgeRealtimeService.pendingPayments();
    const countOfProblemIssues = this.menuBadgeRealtimeService.problemIssues();
    const pendingTasksBadge = !useTaskMobileRoute && countOfPendingTasks ? String(countOfPendingTasks) : undefined;
    const pendingPaymentsBadge = this.canViewPaymentBadge(roles) && countOfPendingPayments ? String(countOfPendingPayments) : undefined;

    const userMenuItems: CustomMenuItem[] = [
      { label: this.translocoService.translate('menu.home'), icon: 'pi pi-desktop', routerLink: justOwner ? '/factura-owner' : '/home' },
      {
        label: this.translocoService.translate('menu.tasks'),
        icon: 'pi pi-check-square',
        routerLink: useTaskMobileRoute ? '/task-mobile' : '/task',
        entity: EntityType.TASK,
        badge: pendingTasksBadge
      },
      {
        label: this.translocoService.translate('menu.issue'),
        icon: 'pi pi-exclamation-circle',
        badge: countOfProblemIssues ? String(countOfProblemIssues) : undefined,
        preserveGroup: true,
        items: [
          {
            label: this.translocoService.translate('menu.issueList'),
            icon: 'pi pi-list',
            routerLink: '/issue',
            entity: EntityType.ISSUE
          },
          {
            label: this.translocoService.translate('menu.myExecutions'),
            icon: 'pi pi-wrench',
            routerLink: '/issue/my-executions',
            entity: EntityType.ISSUE_EXECUTION,
            requiresEmployee: true
          }
        ]
      },
      { label: this.translocoService.translate('menu.owners'), icon: 'pi pi-users', routerLink: '/owner', entity: EntityType.OWNER },
      { label: this.translocoService.translate('menu.guests'), icon: 'pi pi-users', routerLink: '/guest', entity: EntityType.GUEST },
      { label: this.translocoService.translate('menu.properties'), icon: 'pi pi-home', routerLink: '/property', entity: EntityType.PROPERTY },
      {
        label: this.translocoService.translate('menu.reservations'),
        icon: 'pi pi-list',
        routerLink: '/reservation',
        entity: EntityType.RESERVATION
      },
      {
        label: this.translocoService.translate('menu.payments'),
        icon: 'pi pi-euro',
        routerLink: '/payment',
        entity: EntityType.PAYMENT,
        badge: pendingPaymentsBadge
      },
      {
        label: this.translocoService.translate('menu.cashTransfers'),
        icon: 'pi pi-arrow-right-arrow-left',
        routerLink: '/cashtransfer',
        entity: EntityType.CASHTRANSFER
      },
      {
        label: this.translocoService.translate('menu.emails'),
        icon: 'pi pi-envelope',
        routerLink: '/email',
        entity: EntityType.EMAIL
      },
      {
        label: this.translocoService.translate('menu.employees'),
        icon: 'pi pi-id-card',
        items: [
          {
            label: this.translocoService.translate('menu.list'),
            icon: 'pi pi-list',
            routerLink: '/employee',
            entity: EntityType.EMPLOYEE
          },
          {
            label: this.translocoService.translate('menu.salary'),
            icon: 'pi pi-wallet',
            routerLink: '/employee/salary',
            entity: EntityType.EMPLOYEESALARY
          }
        ]
      },
      {
        label: this.translocoService.translate('menu.factura'),
        icon: 'pi pi-receipt',
        routerLink: justOwner ? '/factura-owner' : '/factura',
        entity: EntityType.FACTURA
      },
      {
        label: this.translocoService.translate('menu.cleanings'),
        icon: 'pi pi-sun',
        routerLink: useCleaningMobileRoute ? '/cleaning-mobile' : '/cleaning',
        entity: EntityType.CLEANING,
        badge: countOfPendingCleanings ? String(countOfPendingCleanings) : undefined
      },
      {
        label: this.translocoService.translate('menu.suppliers'),
        icon: 'pi pi-share-alt',
        routerLink: '/supplier',
        entity: EntityType.SUPPLIER
      },
      {
        label: this.translocoService.translate('menu.staffLocations'),
        icon: 'pi pi-building',
        routerLink: '/stafflocation',
        entity: EntityType.STAFFLOCATION
      },
      {
        label: this.translocoService.translate('menu.inventory'),
        icon: 'pi pi-box',
        items: [
          {
            label: this.translocoService.translate('menu.inventoryTransactions'),
            icon: 'pi pi-arrow-right-arrow-left',
            routerLink: '/inventorytransaction',
            entity: EntityType.INVENTORYTRANSACTION
          },
          {
            label: this.translocoService.translate('menu.itemsList'),
            icon: 'pi pi-list',
            routerLink: '/inventoryitem',
            entity: EntityType.INVENTORYITEM
          }
        ]
      },
      {
        label: this.translocoService.translate('menu.report'),
        icon: 'pi pi-chart-bar',
        items: [
          {
            label: this.translocoService.translate('menu.reservations'),
            icon: 'pi pi-list',
            routerLink: '/report/reservations',
            entity: EntityType.REPORT,
            permissionBlock: 'reservations'
          },
          {
            label: this.translocoService.translate('report.payment_control.title'),
            icon: 'pi pi-euro',
            routerLink: '/report/payment-control',
            entity: EntityType.REPORT,
            permissionBlock: 'payment_control'
          },
          {
            label: this.translocoService.translate('report.payments.title'),
            icon: 'pi pi-chart-line',
            routerLink: '/report/payments',
            entity: EntityType.REPORT,
            permissionBlock: 'payments'
          },
          {
            label: this.translocoService.translate('report.owner.menu_title'),
            icon: 'pi pi-wallet',
            routerLink: '/report/owner',
            entity: EntityType.REPORT,
            permissionBlock: 'owners'
          },
          {
            label: this.translocoService.translate('menu.cleanings'),
            icon: 'pi pi-sun',
            routerLink: '/report/cleanings',
            entity: EntityType.REPORT,
            permissionBlock: 'cleanings'
          },
          {
            label: this.translocoService.translate('menu.cleaners'),
            icon: 'pi pi-users',
            routerLink: '/report/cleaners',
            entity: EntityType.REPORT,
            permissionBlock: 'cleaners'
          }
          // {
          //   label: this.translocoService.translate('menu.office'),
          //   icon: 'pi pi-building',
          //   routerLink: '/report/office'
          // }
        ],
        entity: EntityType.REPORT
      },
      {
        label: this.translocoService.translate('menu.settings'),
        icon: 'pi pi-cog',
        roles: [Role.ADMIN],
        items: [
          {
            label: this.translocoService.translate('menu.permissions'),
            icon: 'pi pi-key',
            routerLink: '/setting/permission',
            roles: [Role.ADMIN]
          },
          {
            label: this.translocoService.translate('menu.import'),
            icon: 'pi pi-file-import',
            routerLink: '/setting/import',
            roles: [Role.ADMIN]
          },
          {
            label: this.translocoService.translate('menu.deepCleaningItems'),
            icon: 'pi pi-list-check',
            routerLink: '/setting/deep-cleaning-items',
            roles: [Role.ADMIN]
          },
          {
            label: this.translocoService.translate('menu.responsibilities'),
            icon: 'pi pi-sitemap',
            routerLink: '/setting/responsibilities',
            roles: [Role.ADMIN]
          },
          {
            label: this.translocoService.translate('menu.translations'),
            icon: 'pi pi-globe',
            routerLink: '/translation',
            entity: EntityType.DYNAMICTRANSLATION,
            roles: [Role.ADMIN]
          },
          {
            label: this.translocoService.translate('menu.inventoryTags'),
            icon: 'pi pi-tags',
            routerLink: '/setting/inventory-tags',
            roles: [Role.ADMIN]
          }
        ]
      }
    ];

    if (environment.isDev) {
      userMenuItems.push({
        label: this.translocoService.translate('menu.playground'),
        icon: 'pi pi-play',
        routerLink: '/playground',
        roles: [Role.ADMIN]
      });
    }

    this.menuItems = this.filterVisibleMenuItems(userMenuItems, roles);
  }

  private filterVisibleMenuItems(items: CustomMenuItem[], roles: string[]): MenuItem[] {
    return items
      .filter((item) => this.isVisibleMenuItem(item, roles))
      .map((item) => {
        const childItems = item.items ? this.filterVisibleMenuItems(item.items, roles) : undefined;
        if (childItems?.length === 1 && !item.preserveGroup) {
          return childItems[0];
        }
        return { ...item, items: childItems, badgeStyleClass: 'menu-item-badge' };
      })
      .filter((item) => Boolean(item.routerLink || item.items?.length));
  }

  private isVisibleMenuItem(item: CustomMenuItem, roles: string[]): boolean {
    return (
      (item.roles?.length ? item.roles.some((role) => roles.includes(role)) : true) &&
      (!item.requiresEmployee || !!this.authService.currentUser?.employeeId) &&
      (item.entity
        ? this.permissionService.hasPermission(
            item.entity,
            [PermissionKey.VIEW, PermissionKey.EDIT],
            !item.permissionBlock,
            item.permissionBlock
          )
        : true)
    );
  }

  private canViewPaymentBadge(roles: string[]): boolean {
    return roles.includes(Role.ACCOUNTANT) || roles.includes(Role.TOP_MANAGER);
  }

  private canViewIssueBadge(): boolean {
    return this.permissionService.hasPermission(EntityType.ISSUE, [PermissionKey.VIEW, PermissionKey.EDIT], true);
  }

  updateUserProfile() {
    this.userProfile = [
      {
        label: this.authService.currentUser.name,
        icon: 'pi pi-user',
        items: [
          [
            {
              label: this.authService.currentUser.roles.map((role) => this.translocoService.translate('Role.' + Role[role])).join(', '),
              items: [
                {
                  label: this.translocoService.translate('menu.logout'),
                  icon: 'pi pi-sign-out',
                  command: () => this.logout()
                }
              ]
            }
          ]
        ]
      }
    ];
  }

  async logout() {
    console.log('Logging out...');
    try {
      this.menuBadgeRealtimeService.reset();
      this.realtimeSocketService.disconnect();
      await Session.signOut();
    } catch (error) {
      console.error('Error during logout:', error);
    }
    this.authService.clearLoginRedirect();
    window.location.href = '/auth';
  }
}
